import type { MarketingPage, MethodStep, MemberStory } from "./types";

export const GROUNDWORK_METHOD: MethodStep[] = [
  {
    step: 1,
    title: "TEST",
    description: "10 functional tests score your feet, glutes, and core Level 1–4 to find your weak links.",
  },
  {
    step: 2,
    title: "TRAIN",
    description: "15-minute follow-along routines plus the Strong Feet and Power Glutes courses target your lowest scores.",
  },
  {
    step: 3,
    title: "RETEST",
    description: "Every 6–12 weeks, retake the tests and watch your score climb toward 40/40.",
  },
];

export const MEMBER_STORIES: MemberStory[] = [
  {
    name: "Jordan",
    persona: "Runner",
    quote:
      "I kept getting the same nagging shin pain every training block. The functional tests showed my ankle mobility and single-leg balance were both stuck at Level 1. Three months of daily workouts later, I retested at Level 3 on both — and the shin pain hasn't come back.",
  },
  {
    name: "Priya",
    persona: "Hiker",
    quote:
      "I always assumed my flat feet were just how I was built. The Strong Feet course changed how I think about that completely. My arches still aren't 'fixed' but they do a lot more work than they used to, and long descents don't wreck my knees anymore.",
  },
  {
    name: "Marcus",
    persona: "Desk worker",
    quote:
      "Fifteen minutes a day was the only reason I stuck with anything. I do a workout on my lunch break most days. My glute activation score went from Level 1 to Level 3 in one retest cycle, and my lower back finally stopped complaining by 4pm.",
  },
  {
    name: "Anaya",
    persona: "New parent",
    quote:
      "Between naps and feedings I had no time for a real workout routine. The 15-minute sessions actually fit. Six weeks in, my single-leg balance test went from 10 seconds to over a minute.",
  },
  {
    name: "Walter",
    persona: "55+, daily walker",
    quote:
      "At 58 I figured strength training wasn't really for me anymore. The functional tests gave me a starting point that didn't feel intimidating, and the retest every couple months keeps me motivated in a way a generic workout app never did.",
  },
];

const MEMBER_STORY_DISCLAIMER =
  "Illustrative member stories — Groundwork Academy is free and these examples show typical use, not verified reviews.";

export const MARKETING: Record<string, MarketingPage> = {
  index: {
    heroKicker: "FREE MEMBERSHIP",
    heroHeadline: "Fix your weak links. Build strength from the ground up.",
    heroSub: "15 minutes a day. No equipment. No gym. Free.",
    transformationBullets: [
      "Correct toe misalignment and improve foot function",
      "Build glutes that actually fire when you need them",
      "Improve single-leg balance and stability",
      "Track measurable progress toward a 40/40 score",
      "No gym membership or equipment required",
      "Just 15 minutes a day",
    ],
    statBlocks: [
      { value: "27+", label: "Targeted Workouts" },
      { value: "1–3", label: "Difficulty Levels" },
      { value: "<20", label: "Minutes Each" },
      { value: "100%", label: "At-Home Friendly" },
    ],
    compareRows: [
      { feature: "Finds YOUR specific weak links", us: true, them: false },
      { feature: "15-minute sessions", us: true, them: false },
      { feature: "Teaches the why, not just the how", us: true, them: false },
      { feature: "Measurable retests every 6–12 weeks", us: true, them: false },
      { feature: "Free", us: true, them: false },
    ],
    faqs: [
      {
        question: "Is it really free?",
        answer:
          "Yes. Groundwork Academy is free to join — sign in with Google and you get full access to the functional tests, the workout library, and both courses. There's no trial period, no credit card, and no upsell waiting at the end.",
      },
      {
        question: "Do I need any equipment?",
        answer:
          "No. Every workout and test in the Academy is designed to be done at home with just your bodyweight and a bit of floor space. A few optional tools are covered in the Gear Guide if you want to add variety later, but nothing is required.",
      },
      {
        question: "How much time do I need per day?",
        answer:
          "Most workouts take 10–20 minutes. The idea is that consistency beats intensity — a short session you actually do every day builds more strength over a few months than an hour-long session you skip half the time.",
      },
      {
        question: "I have flat feet or plantar fasciitis — is this for me?",
        answer:
          "Many members join specifically because of issues like these. The functional tests are designed to find exactly where your foundation is weak so you can target it directly. That said, this isn't medical advice — if you have a diagnosed condition or acute pain, check with a healthcare provider before starting.",
      },
      {
        question: "Do I need to train barefoot?",
        answer:
          "It isn't required, but most of the foot-specific drills work best without shoes so your toes and arches can move freely. Whatever you're comfortable with is fine to start.",
      },
      {
        question: "How fast will I see results?",
        answer:
          "Most members notice a difference in balance and control within a few weeks, with clearer jumps in their retest scores at the 6–12 week mark, which is why that's the recommended retest window.",
      },
    ],
    memberStories: MEMBER_STORIES,
    ctaText: "Join Free",
  },
  tests: {
    heroKicker: "STEP 1 OF THE GROUNDWORK METHOD",
    heroHeadline: "The Functional Screen That Finds Your Weak Links",
    heroSub: "Stop guessing and find the root cause of your issues.",
    transformationBullets: [
      "5 foot tests covering splay, balance, mobility, and strength",
      "5 glute & core tests covering activation, strength, and stability",
      "Score yourself Level 1–4 on each test",
      "Your lowest scores become your training priorities",
      "Retest every 6–12 weeks to track real progress",
      "Goal: 40/40",
    ],
    statBlocks: [
      { value: "5", label: "Foot Tests" },
      { value: "5", label: "Glutes & Core Tests" },
      { value: "1–4", label: "Score Levels" },
      { value: "40", label: "Goal Score" },
    ],
    compareRows: [
      { feature: "Finds the root cause, not just symptoms", us: true, them: false },
      { feature: "Objective, repeatable scoring", us: true, them: false },
      { feature: "Tells you exactly what to train next", us: true, them: false },
      { feature: "Free", us: true, them: false },
    ],
    faqs: [
      {
        question: "How long does the full assessment take?",
        answer: "Most members complete all 10 tests in 15–20 minutes once they're familiar with the setup for each one.",
      },
      {
        question: "How often should I retest?",
        answer: "Every 6–12 weeks. Retesting sooner rarely shows a meaningful change; waiting much longer means you lose the chance to adjust your training in response to what you learn.",
      },
      {
        question: "What if I score Level 1 on everything?",
        answer: "That's a completely normal starting point for anyone who has spent years in supportive shoes and a desk chair. It just means every test is a training priority — start with the Level 1 workouts across the board.",
      },
      {
        question: "Do I need someone to watch me perform the tests?",
        answer: "No. Each test includes clear setup instructions and level criteria so you can self-score accurately at home, alone.",
      },
    ],
    memberStories: MEMBER_STORIES.slice(0, 3),
    ctaText: "Join Free",
  },
  workouts: {
    heroKicker: "INCLUDED WITH FREE MEMBERSHIP",
    heroHeadline: "A Strong Foundation in 15 Minutes a Day",
    heroSub: "Follow-along workouts that fit your schedule.",
    transformationBullets: [
      "27+ targeted workouts across Levels 1–3",
      "Filter by the specific skill you want to train",
      "Under 20 minutes per session",
      "100% at-home, equipment-free",
      "Organized by level so you always know where to start",
      "Built to be done daily",
    ],
    statBlocks: [
      { value: "27+", label: "Targeted Workouts" },
      { value: "1–3", label: "Difficulty Levels" },
      { value: "<20", label: "Minutes Each" },
      { value: "100%", label: "At-Home Friendly" },
    ],
    compareRows: [
      { feature: "Organized by functional skill, not muscle group", us: true, them: false },
      { feature: "Short enough to do daily", us: true, them: false },
      { feature: "Tied directly to your test results", us: true, them: false },
      { feature: "Free", us: true, them: false },
    ],
    faqs: [
      { question: "How do I know which level to start at?", answer: "Take the functional tests first. Any test you score Level 1–2 on points you to that skill's Level 1 workouts; as your retest scores climb, move up." },
      { question: "What equipment do I need?", answer: "None. Every workout uses bodyweight only and can be done in a small amount of floor space." },
      { question: "How often should I do these?", answer: "Daily is ideal, but even 3–4 sessions a week produces steady progress. Consistency matters more than frequency." },
      { question: "Can I do these alongside running or the gym?", answer: "Yes — most members treat these as a short daily addition rather than a replacement for other training." },
    ],
    memberStories: MEMBER_STORIES.slice(1, 4),
    ctaText: "Join Free",
  },
  "strong-feet": {
    heroKicker: "INCLUDED WITH FREE MEMBERSHIP",
    heroHeadline: "Build Feet So Strong You Never Worry About Them Again",
    heroSub: "The course behind toe alignment, arch strength, and ankle mobility.",
    transformationBullets: [
      "Understand the 5 Pillars of Foot Function",
      "Learn why conventional shoes weaken your feet",
      "Train toe splay, single-leg stability, and ankle mobility",
      "Build controlled pronation and supination strength",
      "12 lessons across 5 modules",
      "Written lessons you can read in a few minutes each",
    ],
    statBlocks: [
      { value: "5", label: "Modules" },
      { value: "12", label: "Lessons" },
      { value: "5", label: "Pillars of Foot Function" },
      { value: "100%", label: "Free" },
    ],
    compareRows: [
      { feature: "Explains the why behind every drill", us: true, them: false },
      { feature: "Structured, progressive curriculum", us: true, them: false },
      { feature: "Tied to your functional test scores", us: true, them: false },
      { feature: "Free", us: true, them: false },
    ],
    faqs: [
      { question: "How long until I see results?", answer: "Most members notice better balance and less foot fatigue within a few weeks of consistent practice; test scores usually shift meaningfully by the 6–12 week retest." },
      { question: "Will this fix my bunions?", answer: "Strengthening toe splay and arch control can improve alignment and slow progression for many people, but this isn't medical treatment — see a podiatrist for an existing diagnosis." },
      { question: "I have flat feet — can they actually change?", answer: "Arch strength and active control can improve substantially even if your passive arch structure doesn't change shape, and that's what most of this course targets." },
      { question: "How does this connect to the daily workouts?", answer: "Each lesson links to workouts tagged with the relevant skill, so you can move from reading to practicing immediately." },
    ],
    memberStories: MEMBER_STORIES.slice(0, 3),
    ctaText: "Join Free",
  },
  "power-glutes": {
    heroKicker: "INCLUDED WITH FREE MEMBERSHIP",
    heroHeadline: "Build the Engine Behind Every Movement",
    heroSub: "The course behind glute activation, strength, and single-leg power.",
    transformationBullets: [
      "Understand basic glute anatomy and function",
      "Learn to activate dormant glutes",
      "Build strength without any equipment",
      "Develop single-leg power and control",
      "Integrate glute strength into walking, running, and squatting",
      "11 lessons across 5 modules",
    ],
    statBlocks: [
      { value: "5", label: "Modules" },
      { value: "11", label: "Lessons" },
      { value: "0", label: "Equipment Needed" },
      { value: "100%", label: "Free" },
    ],
    compareRows: [
      { feature: "Explains the why behind every drill", us: true, them: false },
      { feature: "Progressive, structured curriculum", us: true, them: false },
      { feature: "Tied to your functional test scores", us: true, them: false },
      { feature: "Free", us: true, them: false },
    ],
    faqs: [
      { question: "What's the reading time per lesson?", answer: "Most lessons take 4–6 minutes to read." },
      { question: "What order should I go through the modules?", answer: "In order — each module builds on the last, starting with why glutes matter and ending with integrating strength into real movement." },
      { question: "How does this course relate to the workouts?", answer: "The workout library has a Glute Activation and Glute Strengthening tag; use those filters alongside this course for the practical training side." },
    ],
    memberStories: MEMBER_STORIES.slice(2, 5),
    ctaText: "Join Free",
  },
};
